import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbars, type DataTableColumnsSettingsConfig } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { getWorkspaceSettings, workspaceSettingsKeys } from '@/services/db/environment/queries';
import { useUpdateUserEnvSettings } from '@/services/db/user-env-settings/mutations';
import { useUserEnvSettingsOptional } from '@/services/db/user-env-settings/queries';

const HIDDEN_ATTRIBUTES = new Set(['created_at', 'name']);
const MASQ_ADMIN_ACCOUNT_ID = 1;

type UseObjectColumnsOptions = {
  workspaceId: number;
  objectTypeId: string;
  sentAttributes: readonly string[];
  isSampleWorkspace: boolean;
};

export function useObjectColumns({
  workspaceId,
  objectTypeId,
  sentAttributes,
  isSampleWorkspace,
}: UseObjectColumnsOptions) {
  const { account, user } = useAuth();
  const snackbars = useSnackbars();
  const settingsAccountId = account.is_masq === true ? MASQ_ADMIN_ACCOUNT_ID : user.account_id;
  const isSampleAccess = account.is_sample_account_access === true;
  const columnKey = `${workspaceId}:${objectTypeId}`;
  const [localColumns, setLocalColumns] = useState<{ key: string; columns: readonly string[] } | null>(null);

  const userSettings = useUserEnvSettingsOptional({
    accountId: settingsAccountId,
    userId: user.id,
    workspaceId,
    enabled: !isSampleAccess,
  });
  const workspaceSettings = useQuery({
    queryKey: workspaceSettingsKeys.detail(workspaceId),
    queryFn: ({ signal }) => getWorkspaceSettings(workspaceId, signal),
    enabled: !isSampleAccess && !isSampleWorkspace,
    staleTime: 300_000,
  });

  const userColumns = userSettings.data?.objects_page?.objectTypeColumns?.[objectTypeId] ?? [];
  const environmentColumns = workspaceSettings.data?.settings?.object_type_columns?.[objectTypeId] ?? [];
  const selectedColumns =
    localColumns?.key === columnKey ? localColumns.columns : userColumns.length > 0 ? userColumns : environmentColumns;
  const availableColumns = useMemo(
    () => [...new Set(sentAttributes)].filter((attribute) => !HIDDEN_ATTRIBUTES.has(attribute)),
    [sentAttributes],
  );
  const availableSet = useMemo(() => new Set(availableColumns), [availableColumns]);
  const displayedColumns = selectedColumns.filter((column) => availableSet.has(column));

  const updateSettings = useUpdateUserEnvSettings({
    scope: { id: `object-columns:${settingsAccountId}:${user.id}:${workspaceId}` },
    onError: () => {
      setLocalColumns(null);
      snackbars.create({
        id: 'objects.columns',
        variant: 'critical',
        message: "Column settings couldn't be saved. Try again.",
      });
    },
  });

  const columnVisibility = Object.fromEntries(
    availableColumns.map((column) => [column, displayedColumns.includes(column)]),
  );
  const onColumnVisibilityChange = (visibility: Record<string, boolean>) => {
    const columns = availableColumns.filter((column) => visibility[column] === true);
    setLocalColumns({ key: columnKey, columns });
    if (isSampleAccess || !userSettings.isSuccess) return;
    updateSettings.mutate({
      accountId: settingsAccountId,
      userId: user.id,
      workspaceId,
      settings: {
        objects_page: {
          objectTypeColumns: {
            ...userSettings.data?.objects_page?.objectTypeColumns,
            [objectTypeId]: columns,
          },
        },
      },
    });
  };

  const columnsSettings: DataTableColumnsSettingsConfig = { maxVisibleColumns: 4 };
  return { availableColumns, columnVisibility, onColumnVisibilityChange, columnsSettings };
}
